import { ICacheManager } from "./ICacheManager";

/**
 * sessionStorage / localStorage を使用するキャッシュマネージャーの基底クラス
 * データのI/O処理のみを実装し、型の変換処理は子クラスに委譲する (抽象メソッド)
 */
export abstract class AbstractWebStorageCacheManager<T> implements ICacheManager<T> {
  protected storageType: "session" | "local"; // sessionStorage または localStorage
  protected cacheKey: string;

  constructor(key: string, storageType: "session" | "local" = "session") {
    this.cacheKey = key;
    this.storageType = storageType;
  }

  private getStorage(): Storage | null {
    // windowオブジェクトへのアクセスをメソッド内に限定
    if (typeof window === "undefined") {
      return null;
    }

    return this.storageType === "session" ? window.sessionStorage : window.localStorage;
  }

  // ICacheManager の抽象メソッド（子クラスで実装必須）
  public abstract serialize(data: T): object;
  public abstract deserialize(jsonObject: unknown): T | null;

  // ------------------------------------
  // I/O処理（すべてのWeb Storageベースのキャッシュで共通）
  // ------------------------------------

  public getCache(): T | null {
    const storage = this.getStorage();
    if (!storage) {
      return null;
    }
    try {
      const item = storage.getItem(this.cacheKey);
      if (item) {
        const jsonObject = JSON.parse(item);
        return this.deserialize(jsonObject);
      }
    } catch (error) {
      console.error("Error reading cache:", error);
    }
    return null;
  }

  public setCache(data: T): void {
    const storage = this.getStorage();
    if (!storage) {
      return;
    }
    try {
      const jsonObject = this.serialize(data);
      storage.setItem(this.cacheKey, JSON.stringify(jsonObject));
    } catch (error) {
      console.error("Error writing cache:", error);
    }
  }

  public clearCache(): void {
    const storage = this.getStorage();
    if (!storage) {
      return;
    }
    storage.removeItem(this.cacheKey);
  }
}
